import { db, collection, addDoc, query, where, getDocs, getDoc, doc, serverTimestamp, updateDoc } from '../firebase';
import { getFIFOImpact } from './inventoryService';

export interface JournalLine {
  accountId: string;
  accountName: string;
  description: string;
  debit: number;
  credit: number;
}

export interface AutoJournal {
  orgId: string;
  date: string;
  narration: string;
  reference: string;
  sourceType: 'invoice' | 'bill' | 'payment' | 'manual';
  sourceId: string;
  status: 'Draft' | 'Posted';
  lines: JournalLine[];
  totalDebit: number;
  totalCredit: number;
  createdBy?: string;
  createdAt?: any;
  idempotencyKey?: string;
  reversalOf?: string;
}

export const getAccountByCode = async (orgId: string, code: string) => {
  const q = query(
    collection(db, 'chart_of_accounts'),
    where('orgId', '==', orgId),
    where('code', '==', code)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as any;
};

export const ensureAccountByCode = async (orgId: string, code: string, defaultData: any, userId: string): Promise<any> => {
  const existing = await getAccountByCode(orgId, code);
  if (existing) return existing;

  console.log(`Core account ${code} missing for org ${orgId}. Creating automatically...`);
  const docRef = await addDoc(collection(db, 'chart_of_accounts'), {
    ...defaultData,
    code,
    orgId,
    createdBy: userId,
    createdAt: serverTimestamp(),
    status: 'active',
    ytd: 0,
    isSystemAccount: true
  });
  
  return { id: docRef.id, ...defaultData, code, orgId };
};

export const getAccountById = async (accountId: string) => {
  if (!accountId) return null;
  const docRef = doc(db, 'chart_of_accounts', accountId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as any;
};

/**
 * Resolves an account from settings. 
 * Tries the value as an ID first, then as a Code.
 * If neither found, creates or returns the default account by code.
 */
export const resolveDefaultAccount = async (orgId: string, value: string, defaultCode: string, defaultData: any, userId: string): Promise<any> => {
  // 1. Try resolving as ID
  if (value && value.length > 10) { // IDs are usually long strings
    const acc = await getAccountById(value);
    if (acc && acc.orgId === orgId) return acc;
  }

  // 2. Try resolving as Code
  if (value) {
    const acc = await getAccountByCode(orgId, value);
    if (acc) return acc;
  }

  // 3. Fallback to default code
  return ensureAccountByCode(orgId, defaultCode, defaultData, userId);
};

export const resolveTaxAccount = async (orgId: string, taxRateName: string, defaultCode: string, defaultData: any, userId: string) => {
  if (taxRateName && taxRateName !== 'None' && taxRateName !== '0%') {
    const q = query(collection(db, 'tax_rates'), where('orgId', '==', orgId), where('name', '==', taxRateName));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const taxRateData = snapshot.docs[0].data();
      if (taxRateData.mappedAccountId) {
        const account = await getAccountById(taxRateData.mappedAccountId);
        if (account) return { id: account.id, name: account.name, code: account.code };
      }
    }
  }
  return ensureAccountByCode(orgId, defaultCode, defaultData, userId);
};

export const createAutoJournal = async (journal: Omit<AutoJournal, 'createdAt'>) => {
  try {
    if (!journal.orgId || !journal.sourceId || !journal.createdBy) {
      throw new Error('Journal requires orgId, sourceId, and createdBy for audit traceability.');
    }
    if (!journal.lines || journal.lines.length < 2) {
      throw new Error('A posted journal requires at least two lines.');
    }

    for (const line of journal.lines) {
      if (!line.accountId) throw new Error('Every journal line requires a valid account.');
      if (![line.debit, line.credit].every(Number.isFinite)) throw new Error('Journal amounts must be finite numbers.');
      if (line.debit < 0 || line.credit < 0) throw new Error('Journal amounts cannot be negative.');
      if ((line.debit > 0) === (line.credit > 0)) {
        throw new Error('Each journal line must contain either a debit or a credit, but not both.');
      }
    }

    const calculatedDebit = journal.lines.reduce((sum, line) => sum + line.debit, 0);
    const calculatedCredit = journal.lines.reduce((sum, line) => sum + line.credit, 0);
    if (Math.abs(calculatedDebit - calculatedCredit) > 0.001) {
      throw new Error(`UnbalancedJournalEntryError: debits ${calculatedDebit} do not equal credits ${calculatedCredit}.`);
    }

    const idempotencyKey = journal.idempotencyKey
      || `${journal.orgId}:${journal.sourceType}:${journal.sourceId}:${journal.reference}:${journal.reversalOf || 'posting'}`;
    const existing = await getDocs(query(collection(db, 'manual_journals'), where('idempotencyKey', '==', idempotencyKey)));
    if (!existing.empty) return existing.docs[0].id;

    const docRef = await addDoc(collection(db, 'manual_journals'), {
      ...journal,
      idempotencyKey,
      totalDebit: calculatedDebit,
      totalCredit: calculatedCredit,
      journalNumber: `AUTO-${Math.floor(100000 + Math.random() * 900000)}`,
      createdAt: serverTimestamp(),
      postedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error creating auto journal:", error);
    throw error;
  }
};

/**
 * Creates immutable reversing entries for every posted journal linked to a source document.
 * Original journal lines remain untouched; reports include the original and reversal, netting to zero.
 */
export const reverseAutoJournalsForSource = async (orgId: string, sourceId: string, userId: string, reason: string) => {
  const snapshot = await getDocs(query(collection(db, 'manual_journals'), where('sourceId', '==', sourceId)));
  const sourceJournals = snapshot.docs.filter(journalDoc => {
    const data = journalDoc.data() as any;
    return data.orgId === orgId && data.status === 'Posted' && !data.reversalOf;
  });

  const reversalIds: string[] = [];
  for (const journalDoc of sourceJournals) {
    const original = journalDoc.data() as any;
    const originalLines: JournalLine[] = original.lines || original.items || [];
    if (originalLines.length < 2) continue;

    const reversalId = await createAutoJournal({
      orgId,
      date: new Date().toISOString().split('T')[0],
      narration: `Reversal: ${original.narration || original.reference} — ${reason}`,
      reference: `REV-${original.reference || journalDoc.id.slice(-6)}`,
      sourceType: original.sourceType || 'manual',
      sourceId,
      status: 'Posted',
      lines: originalLines.map(line => ({
        accountId: line.accountId,
        accountName: line.accountName || '',
        description: `Reversal: ${line.description || reason}`,
        debit: Number(line.credit || 0),
        credit: Number(line.debit || 0),
      })),
      totalDebit: Number(original.totalCredit || 0),
      totalCredit: Number(original.totalDebit || 0),
      createdBy: userId,
      reversalOf: journalDoc.id,
      idempotencyKey: `${orgId}:reversal:${journalDoc.id}`,
    });

    await updateDoc(doc(db, 'manual_journals', journalDoc.id), {
      reversedByJournalId: reversalId,
      reversedAt: serverTimestamp(),
      reversedBy: userId,
      reversalReason: reason,
    });
    reversalIds.push(reversalId);
  }

  return reversalIds;
};

export const postInvoiceJournal = async (orgId: string, invoice: any, userId: string) => {
  const settingsDoc = await getDoc(doc(db, 'settings', orgId));
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const defaults = settings.defaultAccounts || {};

  const salesAccount = await resolveDefaultAccount(orgId, defaults.salesAccount, '4010', { name: 'Sales', type: 'Sales', nature: 'Credit', taxRate: '0%' }, userId);
  const arAccount = await resolveDefaultAccount(orgId, defaults.accountsReceivable, '1100', { name: 'Accounts Receivable', type: 'Current Asset', nature: 'Debit', taxRate: '0%' }, userId);
  const discountAccount = await resolveDefaultAccount(orgId, defaults.discountGiven, '4030', { name: 'Sales Discounts', type: 'Direct Cost', nature: 'Debit', taxRate: '0%' }, userId);
  const taxPayableDefaultCode = defaults.vatOutput || '2100';

  const lines: JournalLine[] = [];
  
  // Debit AR
  lines.push({
    accountId: arAccount.id,
    accountName: arAccount.name,
    description: `Invoice ${invoice.number} - ${invoice.contactName}`,
    debit: invoice.totalAmount,
    credit: 0
  });

  // Credit Income Accounts
  for (const item of invoice.items) {
    const accountId = item.account || salesAccount.id;
    const accountName = item.accountName || salesAccount.name;
    
    lines.push({
      accountId: accountId,
      accountName: accountName,
      description: item.description || `Sales from invoice ${invoice.number}`,
      debit: 0,
      credit: (item.total || 0) - (item.taxAmount || 0)
    });

    if (item.taxAmount > 0) {
      const taxPayableAccount = await resolveTaxAccount(orgId, item.taxRate, taxPayableDefaultCode, { name: 'Tax Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, userId);
      lines.push({
        accountId: taxPayableAccount.id,
        accountName: taxPayableAccount.name,
        description: `Tax from invoice ${invoice.number}`,
        debit: 0,
        credit: item.taxAmount
      });
    }
  }

  let totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  let totalCredit = lines.reduce((s, l) => s + l.credit, 0);

  if (Math.abs(totalDebit - totalCredit) > 0.001) {
    if (totalCredit > totalDebit) {
      lines.push({ accountId: discountAccount.id, accountName: discountAccount.name, description: `Discount/Rounding for invoice ${invoice.number}`, debit: totalCredit - totalDebit, credit: 0 });
      totalDebit = totalCredit;
    } else {
      const roundingAccount = await ensureAccountByCode(orgId, '8990', { name: 'Rounding / Adjustments', type: 'Expense', nature: 'Credit', taxRate: '0%' }, userId);
      lines.push({ accountId: roundingAccount.id, accountName: roundingAccount.name, description: `Rounding for invoice ${invoice.number}`, debit: 0, credit: totalDebit - totalCredit });
      totalCredit = totalDebit;
    }
  }

  return createAutoJournal({
    orgId,
    date: invoice.date,
    narration: `Invoice ${invoice.number} to ${invoice.contactName}`,
    reference: invoice.number,
    sourceType: 'invoice',
    sourceId: invoice.id,
    status: 'Posted',
    lines,
    totalDebit,
    totalCredit,
    createdBy: userId
  });
};

export const postBillJournal = async (orgId: string, bill: any, userId: string) => {
  const settingsDoc = await getDoc(doc(db, 'settings', orgId));
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const defaults = settings.defaultAccounts || {};

  const apAccount = await resolveDefaultAccount(orgId, defaults.accountsPayable, '2010', { name: 'Accounts Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, userId);
  const expenseAccountDefault = await resolveDefaultAccount(orgId, defaults.purchaseAccount, '5020', { name: 'Purchase / Expense', type: 'Expense', nature: 'Debit', taxRate: '0%' }, userId);
  const discountReceivedAccount = await resolveDefaultAccount(orgId, defaults.discountReceived, '6140', { name: 'Purchase Discounts', type: 'Other Income', nature: 'Credit', taxRate: '0%' }, userId);
  const vatInputDefaultCode = defaults.vatInput || '1400';

  const lines: JournalLine[] = [];
  
  // Credit AP
  lines.push({
    accountId: apAccount.id,
    accountName: apAccount.name,
    description: `Bill ${bill.number} from ${bill.contactName}`,
    debit: 0,
    credit: bill.totalAmount
  });

  // Debit Expense Accounts
  for (const item of bill.items) {
    const accountId = item.account || expenseAccountDefault.id;
    const accountName = item.accountName || expenseAccountDefault.name;
    
    lines.push({
      accountId: accountId,
      accountName: accountName,
      description: item.description || `Expense from bill ${bill.number}`,
      debit: (item.total || 0) - (item.taxAmount || 0),
      credit: 0
    });

    if (item.taxAmount > 0) {
      const taxInputAccount = await resolveTaxAccount(orgId, item.taxRate, vatInputDefaultCode, { name: 'Tax Paid (Input)', type: 'Current Asset', nature: 'Debit', taxRate: '0%' }, userId);
      lines.push({
        accountId: taxInputAccount.id,
        accountName: taxInputAccount.name,
        description: `Tax from bill ${bill.number}`,
        debit: item.taxAmount,
        credit: 0
      });
    }
  }

  let totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  let totalCredit = lines.reduce((s, l) => s + l.credit, 0);

  if (Math.abs(totalDebit - totalCredit) > 0.001) {
    if (totalDebit > totalCredit) {
      lines.push({ accountId: discountReceivedAccount.id, accountName: discountReceivedAccount.name, description: `Discount for bill ${bill.number}`, debit: 0, credit: totalDebit - totalCredit });
      totalCredit = totalDebit;
    } else {
      const roundingAccount = await ensureAccountByCode(orgId, '8990', { name: 'Rounding / Adjustments', type: 'Expense', nature: 'Debit', taxRate: '0%' }, userId);
      lines.push({ accountId: roundingAccount.id, accountName: roundingAccount.name, description: `Rounding for bill ${bill.number}`, debit: totalCredit - totalDebit, credit: 0 });
      totalDebit = totalCredit;
    }
  }

  return createAutoJournal({
    orgId,
    date: bill.date,
    narration: `Bill ${bill.number} from ${bill.contactName}`,
    reference: bill.number,
    sourceType: 'bill',
    sourceId: bill.id,
    status: 'Posted',
    lines,
    totalDebit,
    totalCredit,
    createdBy: userId
  });
};

export const postPaymentJournal = async (orgId: string, payment: any, invoiceOrBill: any, userId: string, type: 'invoice' | 'bill') => {
  const settingsDoc = await getDoc(doc(db, 'settings', orgId));
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const defaults = settings.defaultAccounts || {};

  const bankCashCode = defaults.bankCash || '1020';
  const arAccountCode = defaults.accountsReceivable || '1100';
  const apAccountCode = defaults.accountsPayable || '2010';

  // Use the accountId provided in the payment if available, otherwise fallback to settings/default
  let cashAccount = payment.accountId ? await getAccountById(payment.accountId) : null;
  
  if (!cashAccount) {
    cashAccount = await resolveDefaultAccount(orgId, defaults.bankCash, '1020', { 
      name: 'Cash', 
      type: 'Bank', 
      nature: 'Debit', 
      enablePayments: true, 
      description: 'Primary cash and bank account' 
    }, userId);
  }

  if (!cashAccount) throw new Error("Could not find or create cash account for payment posting.");

  const lines: JournalLine[] = [];

  if (type === 'invoice') {
    const arAccount = await resolveDefaultAccount(orgId, defaults.accountsReceivable, '1100', { name: 'Accounts Receivable', type: 'Current Asset', nature: 'Debit', taxRate: '0%' }, userId);
    // Debit Cash, Credit AR
    lines.push({
      accountId: cashAccount.id,
      accountName: cashAccount.name,
      description: `Payment for Invoice ${invoiceOrBill.number}`,
      debit: payment.amount,
      credit: 0
    });
    lines.push({
      accountId: arAccount.id,
      accountName: arAccount.name,
      description: `Payment for Invoice ${invoiceOrBill.number}`,
      debit: 0,
      credit: payment.amount
    });
  } else {
    const apAccount = await resolveDefaultAccount(orgId, defaults.accountsPayable, '2010', { name: 'Accounts Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, userId);
    // Debit AP, Credit Cash
    lines.push({
      accountId: apAccount.id,
      accountName: apAccount.name,
      description: `Payment for Bill ${invoiceOrBill.number}`,
      debit: payment.amount,
      credit: 0
    });
    lines.push({
      accountId: cashAccount.id,
      accountName: cashAccount.name,
      description: `Payment for Bill ${invoiceOrBill.number}`,
      debit: 0,
      credit: payment.amount
    });
  }

  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);

  return createAutoJournal({
    orgId,
    date: payment.date,
    narration: `Payment for ${type} ${invoiceOrBill.number}`,
    reference: payment.reference || invoiceOrBill.number,
    sourceType: 'payment',
    sourceId: payment.id,
    status: 'Posted',
    lines,
    totalDebit,
    totalCredit,
    createdBy: userId
  });
};

export const postCreditNoteJournal = async (orgId: string, creditNote: any, userId: string) => {
  const settingsDoc = await getDoc(doc(db, 'settings', orgId));
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const defaults = settings.defaultAccounts || {};

  const salesReturnAccount = await resolveDefaultAccount(orgId, defaults.salesReturns, '4040', { name: 'Sales Returns', type: 'Sales', nature: 'Debit', taxRate: '0%' }, userId);
  const arAccount = await resolveDefaultAccount(orgId, defaults.accountsReceivable, '1100', { name: 'Accounts Receivable', type: 'Current Asset', nature: 'Debit', taxRate: '0%' }, userId);
  const discountAccount = await resolveDefaultAccount(orgId, defaults.discountGiven || defaults.discountReceived, '4030', { name: 'Sales Discounts', type: 'Sales', nature: 'Debit', taxRate: '0%' }, userId);
  const taxPayableDefaultCode = defaults.vatOutput || '2100';

  const lines: JournalLine[] = [];
  
  // Credit Note: Debit Sales (Returns), Credit AR
  
  // Credit AR
  lines.push({
    accountId: arAccount.id,
    accountName: arAccount.name,
    description: `Credit Note ${creditNote.number} - ${creditNote.contactName}`,
    debit: 0,
    credit: creditNote.totalAmount
  });

  // Debit Income Accounts
  for (const item of creditNote.items) {
    const accountId = item.account || salesReturnAccount.id;
    const accountName = item.accountName || salesReturnAccount.name;
    
    lines.push({
      accountId: accountId,
      accountName: accountName,
      description: item.description || `Sales return from credit note ${creditNote.number}`,
      debit: (item.total || 0) - (item.taxAmount || 0),
      credit: 0
    });

    if (item.taxAmount > 0) {
      const taxPayableAccount = await resolveTaxAccount(orgId, item.taxRate, taxPayableDefaultCode, { name: 'Tax Payable', type: 'Current Liability', nature: 'Credit', taxRate: '0%' }, userId);
      lines.push({
        accountId: taxPayableAccount.id,
        accountName: taxPayableAccount.name,
        description: `Tax from credit note ${creditNote.number}`,
        debit: item.taxAmount,
        credit: 0
      });
    }
  }

  let totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  let totalCredit = lines.reduce((s, l) => s + l.credit, 0);

  if (Math.abs(totalDebit - totalCredit) > 0.001) {
    if (totalCredit > totalDebit) {
      const roundingAccount = await ensureAccountByCode(orgId, '8990', { name: 'Rounding / Adjustments', type: 'Expense', nature: 'Credit', taxRate: '0%' }, userId);
      lines.push({ accountId: roundingAccount.id, accountName: roundingAccount.name, description: `Rounding for credit note ${creditNote.number}`, debit: totalCredit - totalDebit, credit: 0 });
      totalDebit = totalCredit;
    } else {
      lines.push({ accountId: discountAccount.id, accountName: discountAccount.name, description: `Discount/Rounding for credit note ${creditNote.number}`, debit: 0, credit: totalDebit - totalCredit });
      totalCredit = totalDebit;
    }
  }

  return createAutoJournal({
    orgId,
    date: creditNote.date,
    narration: `Credit Note ${creditNote.number} to ${creditNote.contactName}`,
    reference: creditNote.number,
    sourceType: 'invoice',
    sourceId: creditNote.id,
    status: 'Posted',
    lines,
    totalDebit,
    totalCredit,
    createdBy: userId
  });
};

export const postInventoryJournal = async (orgId: string, dn: any, userId: string) => {
  const settingsDoc = await getDoc(doc(db, 'settings', orgId));
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const defaults = settings.defaultAccounts || {};

  const inventoryAccount = await resolveDefaultAccount(orgId, defaults.inventoryAccount, '1200', { name: 'Inventory Asset', type: 'Inventory', nature: 'Debit', taxRate: '0%' }, userId);
  const cogsAccount = await resolveDefaultAccount(orgId, defaults.cogsAccount, '5010', { name: 'Cost of Goods Sold', type: 'Direct Cost', nature: 'Debit', taxRate: '0%' }, userId);

  const lines: JournalLine[] = [];
  let totalCostAmount = 0;

  for (const item of dn.items) {
    const warehouseId = dn.warehouseId || dn.storeId || 'default';
    const { totalCost } = await getFIFOImpact(item.itemId || item.id, item.quantityShipped || item.quantity, warehouseId);
    
    totalCostAmount += totalCost;
  }

  if (totalCostAmount > 0) {
    // Debit COGS, Credit Inventory
    lines.push({
      accountId: cogsAccount.id,
      accountName: cogsAccount.name,
      description: `COGS for Delivery ${dn.id.slice(-6)}`,
      debit: totalCostAmount,
      credit: 0
    });
    lines.push({
      accountId: inventoryAccount.id,
      accountName: inventoryAccount.name,
      description: `Inventory reduction for Delivery ${dn.id.slice(-6)}`,
      debit: 0,
      credit: totalCostAmount
    });

    return createAutoJournal({
      orgId,
      date: new Date().toISOString().split('T')[0],
      narration: `Inventory Dispatch: DN-${dn.id.slice(-6)} (Order: ${dn.invoiceNumber})`,
      reference: dn.invoiceNumber,
      sourceType: 'manual',
      sourceId: dn.id,
      status: 'Posted',
      lines,
      totalDebit: totalCostAmount,
      totalCredit: totalCostAmount,
      createdBy: userId
    });
  }
};
